/**
 * Studio → Bundles → New / Edit — #152.
 *
 * Create a new draft bundle. The form collects identity (slug, name,
 * description, industry, version) and an item list. Items are picked
 * from the tenant's existing workspace_tools, studio_skills, and
 * prompt_templates rows.
 *
 * For v1 the picker is intentionally minimal — a multi-select per kind
 * driven by the existing list endpoints. Richer drag/drop ordering +
 * preview lands in a follow-up.
 *
 * Edit mode is reached via `/studio/bundles/new?editId=<uuid>` from
 * the detail page's Edit button. Loads the existing draft and reuses
 * the same form against PATCH.
 */

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, Package, Save, AlertTriangle, Wrench, BookOpen, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  useBundle,
  useCreateBundle,
  useUpdateBundle,
  type BundleEntityKind,
  type IndustryTag,
} from '@/lib/use-studio-bundles';
import { useWorkspaceTools } from '@/lib/use-workspace-tools';
import { apiClient } from '@/lib/api-client';
import { useQuery } from '@tanstack/react-query';

// Edit mode is signalled via ?editId=<uuid>. The search-schema enforces
// the param is a UUID so a malformed link silently drops back to create.
const SearchSchema = z.object({
  editId: z.string().uuid().optional(),
});

export const Route = createFileRoute('/studio/bundles/new')({
  validateSearch: SearchSchema,
  component: StudioBundleNewPage,
});

const SLUG_RE = /^[a-z0-9][a-z0-9_-]*$/;
const SEMVER_RE = /^\d+\.\d+\.\d+(?:[-+][a-zA-Z0-9.-]+)?$/;

const INDUSTRY_OPTIONS: Array<{ value: IndustryTag; label: string }> = [
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'retail', label: 'Retail' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'energy', label: 'Energy' },
  { value: 'finance', label: 'Finance' },
  { value: 'general', label: 'General' },
];

interface SelectedItem {
  entityKind: BundleEntityKind;
  entityId: string;
  label: string;
}

function StudioBundleNewPage() {
  const navigate = useNavigate();
  const { editId } = useSearch({ from: '/studio/bundles/new' });

  const editingQuery = useBundle(editId);
  const editing = editId ? editingQuery.data?.data : undefined;

  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [industryTag, setIndustryTag] = useState<IndustryTag>('general');
  const [version, setVersion] = useState('1.0.0');
  const [selected, setSelected] = useState<SelectedItem[]>([]);

  // Hydrate form when an edit target is loaded.
  useEffect(() => {
    if (editing) {
      setSlug(editing.slug);
      setName(editing.name);
      setDescription(editing.description);
      setIndustryTag(editing.industryTag as IndustryTag);
      setVersion(editing.version);
      setSelected(
        editing.items.map((it) => {
          const snap = (it.entityVersionSnapshot ?? {}) as {
            displayName?: string;
            name?: string;
          };
          return {
            entityKind: it.entityKind as BundleEntityKind,
            entityId: it.entityId,
            label: snap.displayName || snap.name || it.entityId.slice(0, 8),
          };
        }),
      );
    }
  }, [editing]);

  // Source lists for the picker. The v1 picker covers the three kinds
  // the install path actually supports (tool, skill, prompt). Agent /
  // mcp / kb pickers ship with the install backends in follow-ups.
  const toolsQuery = useWorkspaceTools({ scope: 'tenant' });
  const skillsQuery = useQuery<
    { items: Array<{ id: string; name: string; displayName: string; scope: string }> },
    Error
  >({
    queryKey: ['studio-skills', { scope: 'tenant' }],
    queryFn: () => apiClient.get('/studio-skills?scope=tenant'),
  });
  const promptsQuery = useQuery<
    { data: Array<{ id: string; command: string; title: string; isSystem: boolean }> },
    Error
  >({
    queryKey: ['prompt-templates'],
    queryFn: () => apiClient.get('/templates'),
  });

  const createMutation = useCreateBundle();
  const updateMutation = useUpdateBundle(editId ?? '');

  const tools = useMemo(() => toolsQuery.data?.data ?? [], [toolsQuery]);
  const skills = useMemo(
    () => (skillsQuery.data?.items ?? []).filter((s) => s.scope === 'tenant'),
    [skillsQuery],
  );
  const prompts = useMemo(
    () => (promptsQuery.data?.data ?? []).filter((p) => !p.isSystem),
    [promptsQuery],
  );

  const toggleItem = (item: SelectedItem) => {
    setSelected((prev) => {
      const exists = prev.find(
        (p) => p.entityKind === item.entityKind && p.entityId === item.entityId,
      );
      if (exists) {
        return prev.filter(
          (p) => !(p.entityKind === item.entityKind && p.entityId === item.entityId),
        );
      }
      return [...prev, item];
    });
  };
  const isSelected = (kind: BundleEntityKind, id: string) =>
    selected.some((p) => p.entityKind === kind && p.entityId === id);

  // ── Validation ────────────────────────────────────────────────────
  const slugErr = !slug ? 'Required' : !SLUG_RE.test(slug) ? 'Lowercase letters, digits, dashes, underscores' : null;
  const nameErr = !name ? 'Required' : null;
  const versionErr = !SEMVER_RE.test(version) ? 'Must be semver (x.y.z)' : null;
  const hasErrors = !!(slugErr || nameErr || versionErr);

  const isEditMode = !!editId;
  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSave = async () => {
    if (hasErrors) {
      toast.error('Fix validation errors before saving.');
      return;
    }
    try {
      if (isEditMode && editId) {
        await updateMutation.mutateAsync({
          name,
          description,
          industryTag,
          version,
          items: selected.map((s, i) => ({
            entityKind: s.entityKind,
            entityId: s.entityId,
            sortOrder: i,
          })),
        });
        toast.success('Bundle updated.');
        navigate({ to: '/studio/bundles/$id', params: { id: editId } });
      } else {
        const result = await createMutation.mutateAsync({
          slug,
          name,
          description,
          industryTag,
          version,
          items: selected.map((s, i) => ({
            entityKind: s.entityKind,
            entityId: s.entityId,
            sortOrder: i,
          })),
        });
        toast.success('Bundle created. Add items and publish when ready.');
        navigate({ to: '/studio/bundles/$id', params: { id: result.data.id } });
      }
    } catch (e) {
      toast.error(`Save failed: ${(e as Error).message}`);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b bg-card/50 backdrop-blur">
        <div className="flex items-center gap-2 mb-2">
          <Button
            size="sm"
            variant="ghost"
            className="gap-1.5"
            onClick={() => navigate({ to: '/studio/bundles' })}
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Bundles
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">
              {isEditMode ? 'Edit Bundle' : 'New Bundle'}
            </h1>
          </div>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isPending || hasErrors}
            className="gap-1.5"
          >
            <Save className="h-3.5 w-3.5" />
            {isPending ? 'Saving…' : 'Save Draft'}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4 max-w-4xl">
        {/* Identity */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <h2 className="text-sm font-semibold">Identity</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="bundle-slug">Slug</Label>
                <Input
                  id="bundle-slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase())}
                  placeholder="manufacturing-starter"
                  disabled={isEditMode}
                />
                {slugErr && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {slugErr}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="bundle-version">Version</Label>
                <Input
                  id="bundle-version"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="1.0.0"
                />
                {versionErr && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {versionErr}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="bundle-name">Name</Label>
              <Input
                id="bundle-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Manufacturing Starter Kit"
              />
              {nameErr && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {nameErr}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="bundle-description">Description</Label>
              <Textarea
                id="bundle-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Curated tools, skills, and prompts for a typical discrete manufacturing operation."
                rows={3}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="bundle-industry">Industry</Label>
              <Select
                value={industryTag}
                onValueChange={(v) => setIndustryTag(v as IndustryTag)}
              >
                <SelectTrigger id="bundle-industry" className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Item picker */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">Items ({selected.length})</h2>
              <p className="text-xs text-muted-foreground">
                v1 supports tools, skills, and prompts. Agents / MCP servers /
                knowledge sources land in follow-up releases.
              </p>
            </div>

            <ItemPickerSection
              title="Tools"
              icon={Wrench}
              items={tools.map((t) => ({ id: t.id, label: t.displayName, sub: t.name }))}
              selected={(id) => isSelected('tool', id)}
              onToggle={(id, label) => toggleItem({ entityKind: 'tool', entityId: id, label })}
            />
            <ItemPickerSection
              title="Skills"
              icon={BookOpen}
              items={skills.map((s) => ({ id: s.id, label: s.displayName, sub: s.name }))}
              selected={(id) => isSelected('skill', id)}
              onToggle={(id, label) => toggleItem({ entityKind: 'skill', entityId: id, label })}
            />
            <ItemPickerSection
              title="Prompts"
              icon={MessageSquare}
              items={prompts.map((p) => ({ id: p.id, label: p.title, sub: p.command }))}
              selected={(id) => isSelected('prompt', id)}
              onToggle={(id, label) => toggleItem({ entityKind: 'prompt', entityId: id, label })}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ItemPickerSection({
  title,
  icon: Icon,
  items,
  selected,
  onToggle,
}: {
  title: string;
  icon: typeof Wrench;
  items: Array<{ id: string; label: string; sub: string }>;
  selected: (id: string) => boolean;
  onToggle: (id: string, label: string) => void;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic ml-6">None available in your workspace.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => onToggle(it.id, it.label)}
              className="focus:outline-none"
            >
              <Badge
                variant={selected(it.id) ? 'default' : 'outline'}
                className="cursor-pointer hover:bg-primary/20"
                title={it.sub}
              >
                {it.label}
              </Badge>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
